import React, { useState, useEffect } from 'react';
import { 
  Calendar, 
  Search, 
  RefreshCw,
  TrendingUp,
  Activity,
  ChevronUp,
  ChevronDown
} from 'lucide-react';
import { API_BASE_URL, API_ENDPOINTS, getAuthHeaders } from '../config/api.ts';
import FormSelect from '../components/FormSelect.tsx';

interface ActivityLog {
  _id: string;
  dogId: string;
  dogName: string;
  activityType: 'check_in' | 'check_out';
  timestamp: string;
  performedBy: {
    staffId: string;
    staffName: string;
    staffEmail: string;
    staffPosition: string;
  };
  branch: {
    branchId: string;
    branchName: string;
    branchCode: string;
  };
  owner: {
    ownerId: string;
    ownerName: string;
    ownerEmail: string;
    ownerPhone: string;
  };
  notes: string;
  checkInDetails?: {
    serviceType: string;
    estimatedCheckout?: string;
    specialNeeds: string;
  };
  checkOutDetails?: {
    actualCheckout: string;
    condition: string;
    notes: string;
  };
}

interface ActivityStats {
  totalCheckIns: number;
  totalCheckOuts: number;
  totalActivities: number;
  checkInRate: number;
}

const KopekGirisCikis: React.FC = () => {
  const [activityLogs, setActivityLogs] = useState<ActivityLog[]>([]);
  const [stats, setStats] = useState<ActivityStats>({
    totalCheckIns: 0,
    totalCheckOuts: 0,
    totalActivities: 0,
    checkInRate: 0
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterActivityType, setFilterActivityType] = useState('');
  const [filterBranch, setFilterBranch] = useState('');
  const [filterStaff, setFilterStaff] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);

  const fetchActivityLogs = async () => {
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('adminToken');
      if (!token) {
        throw new Error('Admin token bulunamadı');
      }

      const headers = getAuthHeaders(token);
      
      // Query parametreleri oluştur
      const params = new URLSearchParams();
      if (searchTerm) params.append('search', searchTerm);
      if (filterActivityType) params.append('activityType', filterActivityType);
      if (filterBranch) params.append('branchId', filterBranch);
      if (filterStaff) params.append('staffId', filterStaff);
      if (startDate) params.append('startDate', startDate);
      if (endDate) params.append('endDate', endDate);
      params.append('page', currentPage.toString());
      params.append('limit', '20');

      const response = await fetch(`${API_BASE_URL}${API_ENDPOINTS.DOG_ACTIVITY_LOGS}?${params}`, {
        headers
      });

      const data = await response.json();
      
      if (data.success) {
        setActivityLogs(data.data.logs);
        setTotalPages(data.data.pagination.totalPages);
        setTotalItems(data.data.pagination.totalItems);
      } else {
        throw new Error(data.message || 'Aktivite logları getirilemedi');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Bilinmeyen hata');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      if (!token) return;

      const headers = getAuthHeaders(token);
      
      const params = new URLSearchParams();
      if (filterBranch) params.append('branchId', filterBranch);
      if (startDate) params.append('startDate', startDate);
      if (endDate) params.append('endDate', endDate);

      const response = await fetch(`${API_BASE_URL}${API_ENDPOINTS.DOG_ACTIVITY_LOGS_STATS}?${params}`, {
        headers
      });

      const data = await response.json();
      
      if (data.success) {
        setStats(data.data.stats);
      }
    } catch (error) {
      // Fetch stats error
    }
  };

  useEffect(() => {
    fetchActivityLogs();
    fetchStats();
  }, [currentPage, filterActivityType, filterBranch, filterStaff, startDate, endDate]);

  const formatDate = (dateString: string) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('tr-TR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getActivityTypeText = (type: string) => {
    return type === 'check_in' ? 'Giriş' : 'Çıkış';
  };

  const getActivityTypeColor = (type: string) => {
    return type === 'check_in' ? 'green' : 'red';
  };

  const handleSearch = () => {
    setCurrentPage(1);
    fetchActivityLogs();
    fetchStats();
  };

  const handleClearFilters = () => {
    setSearchTerm('');
    setFilterActivityType('');
    setFilterBranch('');
    setFilterStaff('');
    setStartDate('');
    setEndDate('');
    setCurrentPage(1);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Kedi/Köpek Giriş-Çıkış Geçmişi</h1>
          <p className="text-gray-600">Köpeklerin giriş-çıkış aktivite logları</p>
        </div>
        <div className="flex space-x-2 mt-4 sm:mt-0">
          <button
            onClick={() => { setCurrentPage(1); fetchActivityLogs(); fetchStats(); }}
            className="bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700 transition-colors flex items-center"
          >
            <RefreshCw size={20} className="mr-2" />
            Yenile
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Toplam Giriş</p>
              <p className="text-2xl font-bold text-gray-900">{stats.totalCheckIns}</p>
            </div>
            <div className="text-green-500">
              <ChevronUp size={24} />
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Toplam Çıkış</p>
              <p className="text-2xl font-bold text-gray-900">{stats.totalCheckOuts}</p>
            </div>
            <div className="text-red-500">
              <ChevronDown size={24} />
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Toplam Aktivite</p>
              <p className="text-2xl font-bold text-gray-900">{stats.totalActivities}</p>
            </div>
            <div className="text-blue-500">
              <Activity size={24} />
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Giriş Oranı</p>
              <p className="text-2xl font-bold text-gray-900">%{stats.checkInRate}</p>
            </div>
            <div className="text-purple-500">
              <TrendingUp size={24} />
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
            <input
              type="text"
              placeholder="Kedi/Köpek adı, sahip adı..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          <FormSelect
            value={filterActivityType}
            onChange={(e) => setFilterActivityType(e.target.value)}
            label="Aktivite Tipi"
          >
            <option value="">Tüm Aktiviteler</option>
            <option value="check_in">Giriş</option>
            <option value="check_out">Çıkış</option>
          </FormSelect>
          <div>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="Başlangıç tarihi"
            />
          </div>
          <div>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="Bitiş tarihi"
            />
          </div>
          <div>
            <button
              onClick={handleSearch}
              className="w-full bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
            >
              Ara
            </button>
          </div>
          <div>
            <button
              onClick={handleClearFilters}
              className="w-full bg-gray-500 text-white px-4 py-2 rounded-lg hover:bg-gray-600 transition-colors"
            >
              Temizle
            </button>
          </div>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-md">
          <strong>Hata:</strong> {error}
        </div>
      )}

      {/* Activity Logs List */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="bg-gray-50 px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900 flex items-center">
            <Activity size={20} className="mr-2" />
            Aktivite Logları ({totalItems} kayıt)
          </h2>
        </div>

        {loading ? (
          <div className="p-8 text-center">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            <p className="mt-2 text-gray-600">Aktivite logları yükleniyor...</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Köpek
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Aktivite
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Tarih/Saat
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Personel
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Şube
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Sahip
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {activityLogs.map((log) => (
                  <tr key={log._id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="w-10 h-10 bg-orange-500 rounded-full flex items-center justify-center text-white font-medium">
                          {log.dogName.charAt(0).toUpperCase()}
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900">{log.dogName}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        getActivityTypeColor(log.activityType) === 'green' 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-red-100 text-red-800'
                      }`}>
                        {getActivityTypeText(log.activityType)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                      <div className="flex items-center">
                        <Calendar size={14} className="mr-2 text-gray-500" />
                        {formatDate(log.timestamp)}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{log.performedBy.staffName}</div>
                      <div className="text-sm text-gray-500">{log.performedBy.staffPosition}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{log.branch.branchName}</div>
                      <div className="text-sm text-gray-500">{log.branch.branchCode}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{log.owner ? log.owner.ownerName : 'Silinen Kullanıcı'}</div>
                      <div className="text-sm text-gray-500">{log.owner ? log.owner.ownerPhone : 'Hesap silindi'}</div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {!loading && totalPages > 1 && (
          <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
            <div className="flex-1 flex justify-between sm:hidden">
              <button
                onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                disabled={currentPage === 1}
                className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
              >
                Önceki
              </button>
              <button
                onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                disabled={currentPage === totalPages}
                className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
              >
                Sonraki
              </button>
            </div>
            <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
              <div>
                <p className="text-sm text-gray-700">
                  <span className="font-medium">{(currentPage - 1) * 20 + 1}</span> -{' '}
                  <span className="font-medium">{Math.min(currentPage * 20, totalItems)}</span> /{' '}
                  <span className="font-medium">{totalItems}</span> kayıt
                </p>
              </div>
              <div>
                <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                  <button
                    onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                    disabled={currentPage === 1}
                    className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                  >
                    Önceki
                  </button>
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    const page = Math.max(1, Math.min(totalPages - 4, currentPage - 2)) + i;
                    return (
                      <button
                        key={page}
                        onClick={() => setCurrentPage(page)}
                        className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                          page === currentPage
                            ? 'z-10 bg-blue-50 border-blue-500 text-blue-600'
                            : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                        }`}
                      >
                        {page}
                      </button>
                    );
                  })}
                  <button
                    onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                    disabled={currentPage === totalPages}
                    className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                  >
                    Sonraki
                  </button>
                </nav>
              </div>
            </div>
          </div>
        )}

        {!loading && activityLogs.length === 0 && (
          <div className="text-center py-8">
            <div className="mx-auto mb-4 text-gray-400">
              <Activity size={48} />
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">Aktivite logu bulunamadı</h3>
            <p className="text-gray-600">
              {searchTerm || filterActivityType || startDate || endDate 
                ? 'Arama kriterlerinize uygun aktivite logu bulunamadı.' 
                : 'Henüz aktivite logu bulunmuyor.'}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default KopekGirisCikis;
